import { z } from 'zod';

/**
 * Phase 6 — Integrations Hub DTOs.
 *
 * Wraps the multi-provider installation registry.  See
 * docs/ai-platform/09-ROADMAP.md § 8 + migration 0084.
 *
 * `provider` is a free-form lowercase id with format check;
 * canonical list lives in `_provider-registry.ts` on the BE.
 */

export const IntegrationProviderCategory = z.enum([
  'communication',
  'source_control',
  'calendar',
  'email',
  'project_management',
  'automation',
]);
export type IntegrationProviderCategoryValue = z.infer<typeof IntegrationProviderCategory>;

export const IntegrationStatus = z.enum([
  'active',
  'paused',
  'error',
  'pending_oauth',
]);
export type IntegrationStatusValue = z.infer<typeof IntegrationStatus>;

export const IntegrationAuthKind = z.enum([
  'oauth',
  'manual',
  'webhook_only',
  'native',
]);
export type IntegrationAuthKindValue = z.infer<typeof IntegrationAuthKind>;

const ProviderIdRegex = /^[a-z][a-z0-9_-]*$/;

export const ProviderEntrySchema = z.object({
  id: z.string().regex(ProviderIdRegex),
  name: z.string(),
  category: IntegrationProviderCategory,
  description: z.string(),
  comingSoon: z.boolean(),
  authKind: IntegrationAuthKind,
  /** If the provider already has a working surface elsewhere (e.g.
   *  GitHub per-project tab), Hub renders "Manage →" linking there. */
  existingSurface: z
    .object({ label: z.string(), path: z.string() })
    .optional(),
});
export type ProviderEntryProjection = z.infer<typeof ProviderEntrySchema>;

export const InstalledIntegrationSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  provider: z.string().regex(ProviderIdRegex),
  status: IntegrationStatus,
  config: z.record(z.unknown()),
  hasOauthTokens: z.boolean(),
  scopesGranted: z.array(z.string()).nullable(),
  displayName: z.string().nullable(),
  lastSyncAt: z.string().datetime().nullable(),
  lastError: z.string().nullable(),
  installedBy: z.string().uuid(),
  installedAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type InstalledIntegrationProjection = z.infer<typeof InstalledIntegrationSchema>;

export const InstallIntegrationSchema = z.object({
  provider: z.string().regex(ProviderIdRegex),
  displayName: z.string().min(1).max(120).optional(),
  config: z.record(z.unknown()).optional(),
  /** OAuth tokens (plain JSON).  Service encrypts before storing. */
  oauthTokens: z.record(z.unknown()).optional(),
  scopesGranted: z.array(z.string()).optional(),
});
export type InstallIntegrationInput = z.infer<typeof InstallIntegrationSchema>;

export const UpdateIntegrationSchema = z.object({
  displayName: z.string().min(1).max(120).optional(),
  status: IntegrationStatus.optional(),
  config: z.record(z.unknown()).optional(),
  /** Set to null to remove stored tokens (e.g. revoke). */
  oauthTokens: z.record(z.unknown()).nullable().optional(),
  scopesGranted: z.array(z.string()).optional(),
});
export type UpdateIntegrationInput = z.infer<typeof UpdateIntegrationSchema>;

export const IntegrationsListResponseSchema = z.object({
  items: z.array(InstalledIntegrationSchema),
});
export type IntegrationsListResponse = z.infer<typeof IntegrationsListResponseSchema>;

export const ProvidersCatalogResponseSchema = z.object({
  items: z.array(ProviderEntrySchema),
});
export type ProvidersCatalogResponse = z.infer<typeof ProvidersCatalogResponseSchema>;

// ── Email ingest log ──────────────────────────────────────────────────────

export const EmailIngestLogStatus = z.enum([
  'succeeded',
  'failed',
  'duplicate',
  'pending',
]);
export type EmailIngestLogStatusValue = z.infer<typeof EmailIngestLogStatus>;

export const EmailIngestLogEntrySchema = z.object({
  id: z.string().uuid(),
  receivedAt: z.string().datetime(),
  fromEmail: z.string(),
  fromDisplay: z.string().nullable(),
  subject: z.string().nullable(),
  status: EmailIngestLogStatus,
  errorKind: z.string().nullable(),
  taskId: z.string().uuid().nullable(),
  externalMessageId: z.string().nullable(),
});
export type EmailIngestLogEntryProjection = z.infer<typeof EmailIngestLogEntrySchema>;

export const EmailIngestLogResponseSchema = z.object({
  items: z.array(EmailIngestLogEntrySchema),
});
export type EmailIngestLogResponse = z.infer<typeof EmailIngestLogResponseSchema>;

// ── Importers (Linear / Jira) ─────────────────────────────────────────────

export const ImporterProvider = z.enum(['linear', 'jira']);
export type ImporterProviderValue = z.infer<typeof ImporterProvider>;

export const ImporterConnectInputSchema = z.object({
  provider: ImporterProvider,
  apiToken: z.string().min(8).max(500),
});
export type ImporterConnectInput = z.infer<typeof ImporterConnectInputSchema>;

export const ImporterConnectResponseSchema = z.object({
  provider: ImporterProvider,
  viewer: z.object({
    id: z.string(),
    email: z.string(),
    displayName: z.string(),
  }),
  sources: z.array(
    z.object({
      key: z.string(),
      name: z.string(),
      secondary: z.string().nullable().optional(),
    }),
  ),
});
export type ImporterConnectResponse = z.infer<typeof ImporterConnectResponseSchema>;

export const ImporterPreviewInputSchema = z.object({
  provider: ImporterProvider,
  apiToken: z.string().min(8).max(500),
  sourceKey: z.string().min(1).max(200),
});
export type ImporterPreviewInput = z.infer<typeof ImporterPreviewInputSchema>;

export const ImporterPreviewRowSchema = z.object({
  externalId: z.string(),
  title: z.string(),
  status: z.string().nullable(),
  priority: z.string().nullable(),
  assignee: z.string().nullable(),
});
export type ImporterPreviewRow = z.infer<typeof ImporterPreviewRowSchema>;

export const ImporterPreviewResponseSchema = z.object({
  provider: ImporterProvider,
  sourceKey: z.string(),
  sampleCount: z.number().int().min(0),
  sample: z.array(ImporterPreviewRowSchema),
  suggestedFieldMapping: z.record(z.string()),
  warnings: z.array(z.string()),
});
export type ImporterPreviewResponse = z.infer<typeof ImporterPreviewResponseSchema>;

export const ImporterStartInputSchema = z.object({
  provider: ImporterProvider,
  apiToken: z.string().min(8).max(500),
  sourceKey: z.string().min(1).max(200),
  targetProjectId: z.string().uuid(),
  fieldMapping: z.record(z.string()),
});
export type ImporterStartInput = z.infer<typeof ImporterStartInputSchema>;

export const ImporterStartResponseSchema = z.object({
  jobId: z.string(),
  status: z.literal('queued'),
  note: z.string(),
});
export type ImporterStartResponse = z.infer<typeof ImporterStartResponseSchema>;
